import { API_ENDPOINT } from "@/constants/appConstants";
import { getSiteProps } from "@/utils";

import {
  DownloadResponseType,
  UserDetails,
  splashType,
} from "@/components/common/defaultHeader/DownloadAppBanner/types/banner";
import {
  APIResponse,
  BlockCompanyResponse,
  BlockedCompaniesResponse,
  ChangePasswordResponse,
  DeactivateAccountResponse,
  GetProfileDataResponse,
  LinkedInConsentResponse,
  MasterDataResponse,
  NotificationUpdate,
  NotificationUpdateResponse,
  SocialLinksData,
  UnblockResponse,
  UserContactVisibilityResponse,
  UserProfileVisibilityResponse,
  WhatsappOptInResponse,
} from "@/components/settings/types/setting";

import {
  AttachResumeResponse,
  AutoApplyResponse,
  EditJobPreferencesResponse,
  ProfileSection,
  ResumeBuilderResponse,
  ResumeListResponse,
  UploadResumeResponse,
} from "../types/autoapplyTab";

let host = "";
if (typeof window !== "undefined") {
  host = window.location.host;
}

export const siteProps = getSiteProps(host);
const siteContext = siteProps?.site_map || "rexmonster";
const country = siteProps?.region || "india";
export const getHeaders = (additionalHeaders = {}) => {
  const baseHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    "x-source-site-context": siteContext,
    "x-source-country": country,
  };

  return { ...baseHeaders, ...additionalHeaders };
};

export const getNotificationUpdate =
  async (): Promise<NotificationUpdateResponse> => {
    try {
      const response = await fetch(
        `${API_ENDPOINT(host).middleware.emailPreferences}`,
        {
          method: "GET",
          credentials: "include",
          headers: getHeaders(),
        },
      );

      const data: NotificationUpdateResponse = await response.json();
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const editWhatsappPreferences = async (
  status: boolean | null,
): Promise<WhatsappOptInResponse> => {
  const body = { status };
  try {
    const response = await fetch(
      `${API_ENDPOINT(host).middleware.whatsappOptIn}`,
      {
        method: "POST",
        credentials: "include",
        headers: getHeaders(),
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.status} - ${response.statusText}`);
    }
    const responseData: WhatsappOptInResponse = {
      ok: response.ok,
      redirected: response.redirected,
      status: response.status,
      statusText: response.statusText,
      type: response.type,
      url: response.url,
    };
    return responseData;
  } catch (error) {
    console.error("Error updating user whatsapp:", error);
    throw error;
  }
};

export const notificationUpdate = async (
  payload: any,
): Promise<NotificationUpdate> => {
  const payloadNotification = {
    jobNotification: {
      recommendedJobs: payload.recommendedJobs,
      applicationStatus: payload.applicationStatus,
    },
    profileRelatedNotification: {
      profileViews: payload.profileViews,
      improveProfileStrength: payload.improveProfileStrength,
    },
    recruiterCommunications: payload.recruiterCommunications,
    promotionsAndSpecialOffers: payload.promotionsAndSpecialOffers,
    careerAdviceAndTips: payload.careerAdviceAndTips,
    whatsappStatus: payload.whatsAppNotifications,
  };

  try {
    const response = await fetch(
      `${API_ENDPOINT(host).middleware.emailPreferences}`,
      {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(payloadNotification),
      },
    );
    const data: NotificationUpdate = await response.json();
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const changePassword = async (
  payload: any,
): Promise<ChangePasswordResponse> => {
  try {
    const response = await fetch(
      `${API_ENDPOINT(host).middleware.changePassword}`,
      {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ChangePasswordResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

export const getSocialLinksData = async (): Promise<SocialLinksData> => {
  try {
    const response = await fetch(`${API_ENDPOINT(host).middleware.userUrl}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data: SocialLinksData = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching social links data:", error);
    throw error;
  }
};

export const postLinkSocialAccount = async (
  payload: any,
): Promise<{ status: number; data: APIResponse }> => {
  try {
    const response = await fetch(
      `${API_ENDPOINT(host).middleware.socialLink}`,
      {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(payload),
      },
    );
    const data: APIResponse = await response.json();
    if (!response.ok) {
      throw { response: { status: response.status, data } };
    }
    return {
      status: response.status,
      data,
    };
  } catch (err) {
    console.error("API call failed:", err);
    throw err;
  }
};

export const unLinkSocialAccount = async (
  payload: any,
): Promise<APIResponse> => {
  try {
    const resposne = await fetch(
      `${API_ENDPOINT(host).middleware.socialUnLink}`,
      {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(payload),
      },
    );
    const data: APIResponse = await resposne.json();
    return data;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const deactivateAccount = async (
  payload: any,
): Promise<DeactivateAccountResponse> => {
  try {
    const resposne = await fetch(
      `${API_ENDPOINT(host).middleware.deactivateUser}`,
      {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(payload),
      },
    );
    const data: DeactivateAccountResponse = await resposne.json();
    return data;
  } catch (err: any) {
    console.error(err);
    return err;
  }
};

export const masterDataSearch = async (
  type: string,
  query?: string,
  sendSiteContext?: boolean,
): Promise<MasterDataResponse> => {
  let baseUrl = `${API_ENDPOINT(host).middleware.getMasterData}?appName=srp-ui&type=${type}`;
  // Initialize search parameters
  const searchParams = new URLSearchParams();
  if (query) {
    searchParams.append("name", query);
  }
  if (sendSiteContext) {
    searchParams.append("siteContext", siteContext);
  }
  // Append parameters if they exist
  if (searchParams.toString()) {
    baseUrl += `&${searchParams.toString()}`;
  }
  try {
    const response = await fetch(baseUrl, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

//get user Profile data with fields
export const getProfileData = async (
  profileField: string,
): Promise<GetProfileDataResponse> => {
  if (!profileField || typeof profileField !== "string") {
    throw new Error("Invalid profileField. It must be a non-empty string.");
  }
  const modified = Date.now();
  const queryParams = new URLSearchParams({
    fields: profileField,
    modified: String(modified),
  }).toString();
  const url = `${API_ENDPOINT(host).middleware.profileDetails}?${queryParams}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(
        `HTTP error! status: ${response.status} ${response.statusText}`,
      );
    }
    const data: GetProfileDataResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching profile data", error);
    throw error;
  }
};

// update User Profile Visibility
export const updateUserProfileVisibility = async (
  isVisibleFlag: Boolean,
): Promise<UserProfileVisibilityResponse> => {
  const url = `${API_ENDPOINT(host).middleware.userProfileVisibilityUrl}`;
  const body = { visibleFlag: isVisibleFlag };

  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: getHeaders(),
      credentials: "include",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(
        `HTTP error! status: ${response.status} ${response.statusText}`,
      );
    }

    const data: UserProfileVisibilityResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating user profile visibility:", error);
    throw error;
  }
};

// update User Contact Visibility
export const updateUserContactVisibility = async (
  isVisibleFlag: Boolean,
): Promise<UserContactVisibilityResponse> => {
  const url = `${API_ENDPOINT(host).middleware.userContactVisibilityUrl}`;
  const body = { visibility: isVisibleFlag };
  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: getHeaders(),
      credentials: "include",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(
        `HTTP error! status: ${response.status} ${response.statusText}`,
      );
    }

    const data: UserContactVisibilityResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating user contact visibility:", error);
    throw error;
  }
};

// Get Blocked Companies List
export const getBlockCompaniesList =
  async (): Promise<BlockedCompaniesResponse> => {
    const blockCompaniesListUrl = `${API_ENDPOINT(host).middleware.blockedCompanyListUrl}`;
    try {
      const response = await fetch(blockCompaniesListUrl, {
        method: "GET",
        headers: getHeaders(),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const data: BlockedCompaniesResponse = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching blocked companies list:", error);
      throw error;
    }
  };

// Un-block Selected Company
export const unblockCompany = async (
  id: number | number[],
): Promise<UnblockResponse> => {
  const unblockCompanyUrl = `${API_ENDPOINT(host).middleware.unblock}`;
  const payload = { id: id };

  try {
    const response = await fetch(unblockCompanyUrl, {
      method: "DELETE",
      headers: getHeaders(),
      credentials: "include",
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: UnblockResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
};
// Block Selected Company
export const blockSelectedCompany = async (
  query: any,
): Promise<BlockCompanyResponse> => {
  const formattedId = query?.id?.replace(/^en:/, "");
  const payload = [{ id: formattedId }];

  if (query?.id?.length) {
    const blockCompanyUrl = `${API_ENDPOINT(host).middleware.block}`;

    try {
      const response = await fetch(blockCompanyUrl, {
        method: "POST",
        headers: getHeaders(),
        credentials: "include",
        body: JSON.stringify({ companies: payload }),
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! Status: ${response.status} ${response.statusText}`,
        );
      }

      const data: BlockCompanyResponse = await response.json();
      return data;
    } catch (error) {
      console.error("Error blocking the company:", error);
      throw error;
    }
  }

  throw new Error("Invalid company ID");
};

//linkedin Consent Update for Profile Auto Update
export const linkedinConsentUpdate = async (
  linkedInConsentValue: Boolean | null,
  disableReason: string | null,
): Promise<LinkedInConsentResponse> => {
  const linkedinURL = `${API_ENDPOINT(host).middleware.linkedinConsentV2}`;
  let body = {};
  if (disableReason) {
    body = { linkedInConsent: linkedInConsentValue, reason: disableReason };
  } else {
    body = { linkedInConsent: linkedInConsentValue };
  }

  try {
    const response = await fetch(linkedinURL, {
      method: "POST",
      headers: getHeaders(),
      credentials: "include",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const data: LinkedInConsentResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating user linkedin:", error);
    throw error;
  }
};

export const fetchCmsBannerData = async (params = {}) => {
  const queryParams = new URLSearchParams(params); // Encode parameters correctly
  const url = `${API_ENDPOINT(host).middleware.profileBanner}?${queryParams.toString()}`;
  try {
    const res = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });
    if (!res.ok) {
      const errorResponse = await res.text(); // Get error message for debugging
      throw new Error(
        `Failed to fetch CMS banners: ${res.status} - ${errorResponse}`,
      );
    }
    const response = await res.json();
    return response?.data ?? [];
  } catch (error) {
    console.error("Error fetching CMS banners:", error);
    return [];
  }
};
export const fetchAutoApplyData = async (
  host: string,
): Promise<AutoApplyResponse | null> => {
  if (!host) {
    console.error("Host is missing. Cannot fetch Auto Apply data.");
    return null;
  }
  const url = `${API_ENDPOINT(host).middleware.getAutoApply}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error(
        `HTTP error! Status: ${response.status} ${response.statusText}`,
      );
    }
    const data: AutoApplyResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching Auto Apply data:", error);
    throw error;
  }
};

export const editJobPreferences = async (
  payload: any,
): Promise<EditJobPreferencesResponse> => {
  const url = `${API_ENDPOINT(host).middleware.jobPreferences}`;

  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: getHeaders(),
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(
        `HTTP error! Status: ${response.status} ${response.statusText}`,
      );
    }
    const data: EditJobPreferencesResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error posting jb preferences:", error);
    throw error;
  }
};

export const getPersonalDetails = async (
  host: string,
): Promise<ProfileSection> => {
  if (!host) {
    throw new Error("Host is missing.");
  }

  const queryParams = new URLSearchParams({
    fields: "resumes,job_preferences,personal_details,additional_profiles",
  }).toString();

  const url = `${API_ENDPOINT(host).middleware.profileFieldsUrl}?${queryParams}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error(
        `HTTP error! Status: ${response.status} ${response.statusText}`,
      );
    }
    return (await response.json()) as ProfileSection;
  } catch (error) {
    console.error("Error fetching personal details:", error);
    throw error;
  }
};

export const getDownloadResumeUrl = async (
  profileId: number | undefined,
): Promise<any> => {
  if (!profileId) {
    throw new Error("Profile ID is required for downloading the resume.");
  }
  const url = `${API_ENDPOINT(host).middleware.downloadResume}?profileId=${profileId}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });
    if (!response.ok) {
      throw new Error(
        `Network error! Status: ${response.status} ${response.statusText}`,
      );
    }
    return response;
  } catch (error) {
    console.error("Error downloading resume:", error);
    throw error;
  }
};
export const updateAutoApplyInfo = async (
  payload: any,
): Promise<AutoApplyResponse> => {
  const body = JSON.stringify({
    enabled: payload.isAutoApplyEnabled,
    outreachEmailSubject: payload?.outreachEmailSubject,
    outreachEmailBody: payload?.outreachEmailBody,
  });

  const updateUrl = `${API_ENDPOINT(host).middleware.updateAutoApply}`;

  try {
    const response = await fetch(updateUrl, {
      method: "POST",
      headers: getHeaders(),
      body: body,
    });

    if (!response.ok) {
      throw new Error(
        `HTTP error! Status: ${response.status} ${response.statusText}`,
      );
    }

    const data: AutoApplyResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating auto apply info:", error);
    throw error;
  }
};

export const uploadResume = async (
  file: File,
): Promise<UploadResumeResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  const uploadUrl = API_ENDPOINT(host).middleware.loadResume;
  try {
    const response = await fetch(uploadUrl, {
      method: "POST",
      headers: {
        "x-source-site-context": siteContext,
        "x-source-country": country,
      },
      credentials: "include",
      body: formData,
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Error uploading resume: ${response.status} - ${errorText}`,
      );
    }
    const data: UploadResumeResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error uploading resume:", error);
    throw error;
  }
};

export const getResumeBuilderData = async (
  resumeId: string,
  template: string,
): Promise<ResumeBuilderResponse> => {
  const url = `${API_ENDPOINT(host).middleware.resumeData}?resume_id=${resumeId}&limit=5&scratch=false&template=${template}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Error fetching resume data: ${response.statusText}`);
    }
    const data: ResumeBuilderResponse = await response.json();
    return data;
  } catch (err) {
    console.error("Error fetching resume data:", err);
    throw err;
  }
};

export const getResumeList = async (): Promise<ResumeListResponse> => {
  const url = `${API_ENDPOINT(host).middleware.resumeList}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error(`Error fetching resume list: ${response.statusText}`);
    }
    const data: ResumeListResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching resume list:", error);
    throw error;
  }
};

export const postAttachResume = async (
  resumeId: string,
): Promise<AttachResumeResponse> => {
  const url = `${API_ENDPOINT(host).middleware.attachDefaultResume}?resumeId=${resumeId}`;
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      credentials: "include",
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      throw new Error(`Error attaching resume: ${response.statusText}`);
    }
    const data: AttachResumeResponse = await response.json();
    return data;
  } catch (err) {
    console.error("Error attaching resume:", err);
    throw err;
  }
};

export const userDetails = async (): Promise<UserDetails> => {
  const url = `${API_ENDPOINT(host).middleware.userDetails}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error(`Error attaching resume: ${response.statusText}`);
    }
    const data: UserDetails = await response.json();
    return data;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const splashs = async (): Promise<splashType> => {
  const url = `${API_ENDPOINT(host).middleware.splashs}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Error fetching splash data: ${response.statusText}`);
    }
    const data: splashType = await response.json();
    return data;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const download = async (payload: any): Promise<DownloadResponseType> => {
  try {
    const response = await fetch(`${API_ENDPOINT(host).middleware.download}`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data: DownloadResponseType = await response.json();
    return data;
  } catch (error) {
    console.error("Download API error:", error);
    throw error;
  }
};
